import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { TrendingUp, DollarSign, Loader2, AlertTriangle, BarChart3, Clock, History, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Projection {
  bear: number;
  base: number;
  bull: number;
  rationale: string;
}

interface PriceData {
  asset_type: string;
  current_prices: Record<string, number>;
  key_drivers: string[];
  projections: Record<string, Record<string, Projection>>;
}

interface Props {
  topic: string;
  modelResults: any[];
}

const CURRENCIES = ['USD', 'EUR', 'CHF'] as const;
const HORIZONS = ['1W', '1M', '3M', '6M'] as const;
const HORIZON_LABELS: Record<string, string> = {
  '1W': '1 Week',
  '1M': '1 Month',
  '3M': '3 Months',
  '6M': '6 Months',
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  CHF: 'CHF ',
};

const CURRENCY_COLORS: Record<string, string> = {
  USD: 'hsl(142, 71%, 45%)',
  EUR: 'hsl(220, 70%, 50%)',
  CHF: 'hsl(0, 84%, 60%)',
};

export const PriceExpectationPanel = ({ topic, modelResults }: Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PriceData | null>(null);
  const [activeCurrency, setActiveCurrency] = useState<string>('USD');
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [savedProjections, setSavedProjections] = useState<Array<{ id: string; created_at: string; topic: string }>>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Load saved projections for this topic on mount
  useEffect(() => {
    const loadSaved = async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) return;
      const { data: rows } = await supabase
        .from('price_projections')
        .select('id, created_at, topic, asset_type, current_prices, projections, key_drivers, consensus_sentiment, consensus_confidence')
        .eq('topic', topic)
        .order('created_at', { ascending: false })
        .limit(10);
      if (rows && rows.length > 0) {
        setSavedProjections(rows.map(r => ({ id: r.id, created_at: r.created_at, topic: r.topic })));
        // Auto-load the latest
        const latest = rows[0];
        setData({
          asset_type: latest.asset_type || 'stock',
          current_prices: latest.current_prices as any,
          key_drivers: latest.key_drivers as any,
          projections: latest.projections as any,
        });
        setGeneratedAt(latest.created_at);
      }
    };
    loadSaved();
  }, [topic]);

  const loadProjection = async (id: string) => {
    const { data: row } = await supabase
      .from('price_projections')
      .select('*')
      .eq('id', id)
      .single();
    if (row) {
      setData({
        asset_type: row.asset_type || 'stock',
        current_prices: row.current_prices as any,
        key_drivers: row.key_drivers as any,
        projections: row.projections as any,
      });
      setGeneratedAt(row.created_at);
      setShowHistory(false);
    }
  };

  const saveProjection = async (priceData: PriceData) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) return;
    const { error: saveErr } = await supabase.from('price_projections').insert({
      user_id: session.session.user.id,
      topic,
      asset_type: priceData.asset_type,
      current_prices: priceData.current_prices,
      projections: priceData.projections,
      key_drivers: priceData.key_drivers,
      consensus_sentiment: parseFloat(consensusData?.consensusSentiment || '0'),
      consensus_confidence: parseFloat(consensusData?.consensusConfidence || '0'),
    } as any);
    if (saveErr) {
      console.error('Failed to save projection:', saveErr);
    } else {
      // Refresh saved list
      const { data: rows } = await supabase
        .from('price_projections')
        .select('id, created_at, topic')
        .eq('topic', topic)
        .order('created_at', { ascending: false })
        .limit(10);
      if (rows) setSavedProjections(rows.map(r => ({ id: r.id, created_at: r.created_at, topic: r.topic })));
    }
  };

  const consensusData = useMemo(() => {
    const successResults = modelResults.filter(r => r.status === 'success' && r.result);
    if (successResults.length === 0) return null;

    const sentiments = successResults.map(r => {
      const s = r.result?.overall_sentiment?.toLowerCase() || 'neutral';
      const map: Record<string, number> = { very_bearish: -2, bearish: -1, neutral: 0, bullish: 1, very_bullish: 2 };
      return map[s] ?? 0;
    });
    const avgSentiment = sentiments.reduce((a, b) => a + b, 0) / sentiments.length;

    const confidences = successResults.map(r => r.result?.confidence_score ?? 50);
    const avgConfidence = confidences.reduce((a, b) => a + b, 0) / confidences.length;

    const allSignals = successResults.flatMap(r => r.result?.signals || []);

    const horizonBreakdown: Record<string, any> = {};
    successResults.forEach(r => {
      (r.result?.horizon_analysis || []).forEach((h: any) => {
        const key = h.timeframe || h.horizon || 'Other';
        if (!horizonBreakdown[key]) horizonBreakdown[key] = [];
        horizonBreakdown[key].push({
          sentiment: h.sentiment,
          bias_score: h.bias_score,
          factors: h.key_factors || h.factors || [],
        });
      });
    });

    return {
      consensusSentiment: avgSentiment.toFixed(2),
      consensusConfidence: avgConfidence.toFixed(0),
      signals: allSignals.slice(0, 15),
      horizonBreakdown,
    };
  }, [modelResults]);

  const fetchExpectations = async () => {
    if (!consensusData) return;
    setLoading(true);
    setError(null);

    try {
      const { data: respData, error: fnError } = await supabase.functions.invoke('price-expectations', {
        body: {
          topic,
          ...consensusData,
        },
      });

      if (fnError) throw fnError;
      if (respData?.error) throw new Error(respData.error);

      setData(respData);
      const now = new Date().toISOString();
      setGeneratedAt(now);
      toast({ title: 'Price expectations generated', description: `Projections for ${topic} across 3 currencies and 4 horizons.` });

      // Save to database
      await saveProjection(respData);
    } catch (err: any) {
      console.error('Price expectations error:', err);
      setError(err.message || 'Failed to generate price expectations');
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Build fan chart data for a specific currency
  const fanChartData = useMemo(() => {
    if (!data) return [];
    const currency = activeCurrency;
    const currentPrice = data.current_prices?.[currency] ?? 0;

    const points = [
      { name: 'Now', base: currentPrice, bull: currentPrice, bear: currentPrice, bandUpper: 0, bandLower: 0 },
    ];

    HORIZONS.forEach(h => {
      const proj = data.projections?.[h]?.[currency];
      if (proj) {
        points.push({
          name: HORIZON_LABELS[h],
          base: proj.base,
          bull: proj.bull,
          bear: proj.bear,
          bandUpper: proj.bull - proj.base,
          bandLower: proj.base - proj.bear,
        });
      }
    });

    return points;
  }, [data, activeCurrency]);

  // Multi-currency comparison data (base case)
  const multiCurrencyData = useMemo(() => {
    if (!data) return [];
    const points: any[] = [{ name: 'Now' }];
    CURRENCIES.forEach(c => {
      points[0][c] = data.current_prices?.[c] ?? 0;
    });

    HORIZONS.forEach(h => {
      const point: any = { name: HORIZON_LABELS[h] };
      CURRENCIES.forEach(c => {
        point[c] = data.projections?.[h]?.[c]?.base ?? 0;
      });
      points.push(point);
    });

    return points;
  }, [data]);

  const formatPrice = (val: number, currency: string) => {
    const sym = CURRENCY_SYMBOLS[currency] || '';
    return `${sym}${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const pctChange = (from: number, to: number) => {
    if (!from) return '—';
    const pct = ((to - from) / from) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  };

  if (!consensusData || modelResults.filter(r => r.status === 'success').length === 0) return null;

  return (
    <Card className="border-primary/20 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Price Expectations — {topic}
            </CardTitle>
            <CardDescription className="flex items-center gap-2 flex-wrap">
              Consensus-driven projections in EUR, USD & CHF across 4 time horizons
              {generatedAt && (
                <Badge variant="outline" className="text-[10px] gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(generatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Badge>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {savedProjections.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="gap-1.5 text-xs">
                <History className="h-3.5 w-3.5" />
                History ({savedProjections.length})
              </Button>
            )}
            {!data && (
              <Button onClick={fetchExpectations} disabled={loading} className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                {loading ? 'Generating...' : 'Generate Projections'}
              </Button>
            )}
            {data && (
              <Button variant="outline" size="sm" onClick={fetchExpectations} disabled={loading} className="gap-1.5">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                Refresh
              </Button>
            )}
          </div>
        </div>
        {showHistory && savedProjections.length > 0 && (
          <div className="mt-3 border rounded-md divide-y max-h-40 overflow-y-auto">
            {savedProjections.map(p => (
              <div
                key={p.id}
                className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors text-sm"
              >
                <button
                  onClick={() => loadProjection(p.id)}
                  className="flex-1 text-left flex items-center justify-between mr-2"
                >
                  <span className="text-foreground font-medium">{p.topic}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(p.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const { error } = await supabase.from('price_projections').delete().eq('id', p.id);
                    if (error) {
                      toast({ title: 'Error', description: 'Failed to delete projection', variant: 'destructive' });
                    } else {
                      setSavedProjections(prev => prev.filter(s => s.id !== p.id));
                      if (generatedAt && data) {
                        // If we deleted the currently viewed one, clear it
                        const remaining = savedProjections.filter(s => s.id !== p.id);
                        if (remaining.length > 0) {
                          loadProjection(remaining[0].id);
                        } else {
                          setData(null);
                          setGeneratedAt(null);
                        }
                      }
                      toast({ title: 'Projection deleted' });
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardHeader>

      {error && (
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </CardContent>
      )}

      {data && (
        <CardContent className="space-y-6">
          {/* Key Drivers */}
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground font-medium">Key Drivers:</span>
            {data.key_drivers?.map((d, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{d}</Badge>
            ))}
            <Badge variant="outline" className="text-xs capitalize">{data.asset_type}</Badge>
          </div>

          {/* Currency Toggle + Fan Chart */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">Projection Fan Chart</h4>
              <div className="flex gap-1">
                {CURRENCIES.map(c => (
                  <Button
                    key={c}
                    size="sm"
                    variant={activeCurrency === c ? 'default' : 'outline'}
                    className="text-xs h-7 px-3"
                    onClick={() => setActiveCurrency(c)}
                  >
                    {c}
                  </Button>
                ))}
              </div>
            </div>

            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={fanChartData} margin={{ top: 10, right: 30, bottom: 5, left: 10 }}>
                  <defs>
                    <linearGradient id="bullGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="bearGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0.05} />
                      <stop offset="100%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    className="fill-muted-foreground"
                    tickFormatter={(v) => `${CURRENCY_SYMBOLS[activeCurrency]}${v}`}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: 12,
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number, name: string) => [formatPrice(value, activeCurrency), name]}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="bull" stroke="hsl(142, 71%, 45%)" fill="url(#bullGrad)" strokeWidth={1.5} strokeDasharray="4 2" name="Bull Case" dot={{ r: 3 }} />
                  <Area type="monotone" dataKey="base" stroke="hsl(var(--primary))" fill="none" strokeWidth={2.5} name="Base Case" dot={{ r: 4 }} />
                  <Area type="monotone" dataKey="bear" stroke="hsl(0, 84%, 60%)" fill="url(#bearGrad)" strokeWidth={1.5} strokeDasharray="4 2" name="Bear Case" dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Multi-Currency Comparison */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              Multi-Currency Base Case Comparison
            </h4>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={multiCurrencyData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: 12,
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number, name: string) => [formatPrice(value, name), name]}
                  />
                  <Legend />
                  {CURRENCIES.map(c => (
                    <Line key={c} type="monotone" dataKey={c} stroke={CURRENCY_COLORS[c]} strokeWidth={2} dot={{ r: 4 }} name={c} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary Table */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Detailed Projections Table</h4>
            <Tabs defaultValue="USD" className="w-full">
              <TabsList className="mb-2">
                {CURRENCIES.map(c => (
                  <TabsTrigger key={c} value={c} className="text-xs">{c}</TabsTrigger>
                ))}
              </TabsList>
              {CURRENCIES.map(c => (
                <TabsContent key={c} value={c}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-muted-foreground font-medium">Horizon</th>
                          <th className="text-right py-2 px-3 text-rose-500 font-medium">Bear</th>
                          <th className="text-right py-2 px-3 text-foreground font-semibold">Base</th>
                          <th className="text-right py-2 px-3 text-emerald-500 font-medium">Bull</th>
                          <th className="text-right py-2 px-3 text-muted-foreground font-medium">Δ Base</th>
                          <th className="text-left py-2 px-3 text-muted-foreground font-medium">Rationale</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b bg-muted/30">
                          <td className="py-2 px-3 font-medium">Current</td>
                          <td className="text-right py-2 px-3" colSpan={3}>
                            <span className="font-semibold">{formatPrice(data.current_prices?.[c] ?? 0, c)}</span>
                          </td>
                          <td className="text-right py-2 px-3">—</td>
                          <td className="py-2 px-3 text-muted-foreground text-xs">Current estimated price</td>
                        </tr>
                        {HORIZONS.map(h => {
                          const proj = data.projections?.[h]?.[c];
                          if (!proj) return null;
                          const current = data.current_prices?.[c] ?? 0;
                          const change = pctChange(current, proj.base);
                          const isPositive = proj.base >= current;
                          return (
                            <tr key={h} className="border-b hover:bg-muted/20 transition-colors">
                              <td className="py-2 px-3 font-medium">{HORIZON_LABELS[h]}</td>
                              <td className="text-right py-2 px-3 text-rose-500">{formatPrice(proj.bear, c)}</td>
                              <td className="text-right py-2 px-3 font-semibold">{formatPrice(proj.base, c)}</td>
                              <td className="text-right py-2 px-3 text-emerald-500">{formatPrice(proj.bull, c)}</td>
                              <td className={`text-right py-2 px-3 font-medium ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                                {change}
                              </td>
                              <td className="py-2 px-3 text-muted-foreground text-xs max-w-xs truncate" title={proj.rationale}>
                                {proj.rationale}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-muted-foreground text-center italic">
            Price projections are AI-generated estimates based on consensus sentiment analysis. They do not constitute financial advice.
            Actual prices may differ significantly from projections.
          </p>
        </CardContent>
      )}
    </Card>
  );
};
